import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import MediaFile, { FileType } from '@interfaces/MediaFile';
import { Modal } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import cn from 'classnames';
import css from './FullImageModal.module.less';

const ImageModal: FunctionComponent<{ onClose: () => void, files: MediaFile[] }> = ({ onClose, files }) => {
  const mediaFiles = useMemo(() => {
    return files.map(file => {
      if (file.fileType === FileType.Image) {
        return <img
          alt=''
          key={file?.image?.fileId}
          src={file.image?.url || ''}
          className={css.fileImage}
        />;
      }
      if (file.fileType === FileType.Video) {
        return <video controls className={css.fileVideo} key={file.fileId} src={file.url} />;
      }
      return null;
    });
  }, [files]);

  const [indexActive, setIndexActive] = useState(0);
  const mediaFile = useMemo(() => {
    return mediaFiles[indexActive];
  }, [indexActive, mediaFiles]);

  const isSeveral = useMemo(() => {
    return mediaFiles.length > 1;
  }, [mediaFiles]);

  const next = useCallback(() => {
    setIndexActive(index => {
      if (index + 1 === mediaFiles.length) {
        return 0;
      }
      return index + 1;
    });
  }, [mediaFiles]);

  const prev = useCallback(() => {
    setIndexActive(index => {
      if (index - 1 < 0) {
        return mediaFiles.length - 1;
      }
      return index - 1;
    });
  }, [mediaFiles]);

  const keydown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'ArrowLeft') {
      prev();
    }
    if (e.key === 'ArrowRight') {
      next();
    }
  }, [prev, next]);

  useEffect(() => {
    if (isSeveral) {
      document.addEventListener('keydown', keydown);
      return () => document.removeEventListener('keydown', keydown);
    }
  }, [keydown, isSeveral]);

  const selectMedia = useCallback((index) => {
    return () => setIndexActive(index);
  }, []);

  return <Modal
    visible={true}
    onOk={onClose}
    onCancel={onClose}
    width={1000}
  >
    <div className={css.fileView}>
      {
        isSeveral ?
          (<>
            <div className={css.fileViewCarousel} onClick={prev}>
              <LeftOutlined />
            </div>
            <div className={cn(css.fileViewContainer, css.isSeveral)}>
              <div className={css.carousel}>
                {mediaFiles.map((value, index) => {
                  return <span
                    className={cn(css.carouselItem, index === indexActive ? css.action : '')}
                    onClick={selectMedia(index)}
                    key={index}
                  />;
                })}
              </div>
              {mediaFile}
            </div>
            <div className={css.fileViewCarousel} onClick={next}>
              <RightOutlined />
            </div>
          </>) : (
            <div className={css.fileViewContainer}>
              {mediaFile}
            </div>
          )
      }
    </div>
  </Modal>;
};

export default ImageModal;
